import PaymentMethodCategory from '@sunbit-dev/common/enums/cardApply/PaymentMethodCategory';
import PaymentMethodTypes from '@sunbit-dev/common/enums/PaymentMethodTypes';
import { validateDebitCardDetails } from '@sunbit-dev/common/helpers/debitCard';
import { isEmpty } from '@sunbit-dev/common/helpers/isEmpty';
import { ordinal } from '@sunbit-dev/common/helpers/numbers';
import { handleRoutingNumValidationError, validateRccDetails } from '@sunbit-dev/common/helpers/rcc';
import { PaymentMethodProfile } from '@sunbit-dev/common/models/cardApply/IExistingPaymentMethod';
import IPaymentDetailsSubmit from '@sunbit-dev/common/models/cardApply/IPaymentDetailsSubmit';
import IDebitCardDetails from '@sunbit-dev/common/models/IDebitCardDetails';
import IRccDetails from '@sunbit-dev/common/models/IRccDetails';
import React from 'react';
import styled from 'styled-components';
import dataOpen from '../../../../helpers/dataOpen';
import emptyFunction from '../../../../helpers/emptyFunction';
import { CreditCardCVV } from '../../../ui/creditCardCVV/creditCardCVV';
import CreditCardForm from '../../../ui/creditCardForm/CreditCardForm';
import { Dropdown } from '../../../ui/dropdown/Dropdown';
import CardApplyLinkButton from '../../components/cardApplyLinkButton/CardApplyLinkButton';
import RadioGroup from '../../../ui/radio/RadioGroup';
import RccForm from '../../../ui/rccForm/RccForm';
import { materialIcon } from '../../../ui/styled/utils';
import Tooltip, { TooltipPosition } from '../../../ui/tooltip/Tooltip';
import AgreeButton from '../../components/agreeButton/AgreeButton';
import CommonSection from '../../components/commonSection/CommonSection';
import LegalText from '../../components/legalText/LegalText';
import SectionTitle from '../../components/sectionTitle/SectionTitle';
import Title from '../../components/title/Title';
import rccText from '../../../../constants/reusesText';
import { PrivacyOptions } from '@sunbit-dev/common/enums/Datadog';

const supportingCvvInExistingPaymentMethod = false;

// ############################ Styles ###########################
// tslint:disable:variable-name

const SPaymentDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const SCollapsingCategory = styled.section(
  ({ theme }) => `
  box-sizing: border-box;
  padding: ${theme.spacing.large}px ${0}px ${theme.spacing.large}px 0;
  cursor: pointer;
  &:not(:first-child) {
    border-top: 2px solid ${theme.colors.border};
  }
  @media only screen and ${theme.device.laptopS} {
    padding: ${theme.spacing.larger}px ${0}px ${theme.spacing.larger}px 0;
  }
`
);

const SCollapsingCategoryTitle = styled.section<{ isOpen: boolean }>(
  ({ theme, isOpen }) => `
  user-select: none;
  position: relative;
  padding-left: ${theme.spacing.larger2}px;
  &:before {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translate(0, -50%);
    ${materialIcon(isOpen ? 'radio_button_checked' : 'radio_button_unchecked', 30, theme.colors.primary)}
  }
  &:after {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(0, -50%);
    ${materialIcon(isOpen ? 'remove' : 'add', 30, theme.colors.primary)}
  }
`
);

const SCollapsingContent = styled.section<{ isOpen: boolean }>(
  ({ isOpen, theme }) => `
  display: ${isOpen ? 'block' : 'none'};
  padding-top: ${theme.spacing.large}px;
`
);

const SExistingPaymentMethodLabel = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  min-height: 30px;
  color: ${theme.colors.primary};
  font-weight: ${theme.font.weight.normal};
  font-size: ${theme.font.size.smallMedium};
  @media only screen and ${theme.device.laptopS} {
    font-size: ${theme.font.size.medium};
  }
`
);

const SExistingPaymentMethodCVV = styled(CreditCardCVV)(
  ({ theme }) => `
  margin-left: ${theme.spacing.medium}px;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0 !important;
  @media only screen and ${theme.device.laptopS} {
    margin-left: 20px;
  }
  input {
    padding: 2px;
    font-size: 16px;
    max-width: 100px;
    min-width: 50px;
  }
  .inputLabel {
    margin: 0;
    margin-right: 10px;
    font-size: 16px;
  }
`
);

const SDisclaimerLegalText = styled(LegalText)(({ theme }) => `font-size: ${theme.font.size.medium}`);

const SDisclaimerSmallLegalText = styled(LegalText)(({ theme }) => `font-size: ${theme.font.size.smallMedium};`);

const SDisclaimerBoldSpan = styled.span(({ theme }) => `font-weight: ${theme.font.weight.bold};`);

const SDueDateSection = styled.section(() => '');

const SDueDateContent = styled.div`
  max-width: 120px;
`;

const SExistingPaymentMethodsWrap = styled.div(
  ({ theme }) => `
  padding-left: ${theme.spacing.large}px;
`
);

const SExistingPaymentMethodsInstructions = styled.div(
  ({ theme }) => `
  margin-bottom: ${theme.spacing.medium}px;
  color: ${theme.colors.primary};
  font-weight: ${theme.font.weight.bold};
  font-size: ${theme.font.size.smallMedium};
  @media only screen and ${theme.device.laptopS} {
    font-size: ${theme.font.size.medium};
  }
`
);

const SExisitingPaymentMethodsRadios = styled(RadioGroup)(
  ({ theme }) => `
  label {
    background-color: ${theme.colors.whiteGray};
    padding: ${theme.spacing.medium}px;
    @media only screen and ${theme.device.laptopS} {
      padding: ${theme.spacing.medium}px ${theme.spacing.mediumLarger}px;
    }
  }
`
);

const SRccFormWrap = styled.div(
  () => `
  max-width: 500px;
`
);

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface IPaymentDetailsProps {
  existingPaymentMethods: PaymentMethodProfile[];
  militaryLendingAct: boolean;
  recurringPaymentsData: string;
  applicationId: number;
  referringPartner: string;
  selectedPaymentMethodCategory?: PaymentMethodCategory;
  submit: (paymentDetailsSubmit: IPaymentDetailsSubmit) => void;
  onDueDateChangeMiddleware?: (onChange: Function) => (value: number) => void;
}

interface ICollapsingCategoryProps {
  isOpen: boolean;
  title: React.ReactNode;
  onClick: () => void;
  dataAt: string;
}

// tslint:disable-next-line:variable-name
const CollapsingCategory: React.FC<ICollapsingCategoryProps> = (props) => (
  <SCollapsingCategory data-at={props.dataAt} onClick={!props.isOpen ? props.onClick : emptyFunction}>
    <SCollapsingCategoryTitle data-at={`${props.dataAt}-title`} isOpen={props.isOpen}>
      <SectionTitle spacing={0}>{props.title}</SectionTitle>
    </SCollapsingCategoryTitle>
    <SCollapsingContent data-at={`${props.dataAt}-content`} isOpen={props.isOpen}>
      {props.children}
    </SCollapsingContent>
  </SCollapsingCategory>
);

interface IExistingPaymentMethodsProps {
  existingPaymentMethods: PaymentMethodProfile[];
  selected?: PaymentMethodProfile;
  existingPaymentMethodCVV: string;
  onExistingPaymentMethodCVVChanged: (value: string) => void;
  onSelect: (existingPaymentMethods: PaymentMethodProfile) => void;
}

// tslint:disable-next-line:variable-name
const ExistingPaymentMethods: React.FC<IExistingPaymentMethodsProps> = (props) => {
  const options = props.existingPaymentMethods
    .filter((existingPaymentMethod) => [PaymentMethodTypes.DEBIT, PaymentMethodTypes.RCC].indexOf(existingPaymentMethod.paymentMethodType) >= 0)
    .sort((a, b) => (a.paymentMethodType < b.paymentMethodType ? -1 : 1))
    .map((existingPaymentMethod, index) => {
      const label =
        existingPaymentMethod.paymentMethodType === PaymentMethodTypes.DEBIT ? (
          <SExistingPaymentMethodLabel data-dd-privacy={PrivacyOptions.Mask} data-at="ExistingPaymentMethodDebitOption">
            {`Debit card ending in ${existingPaymentMethod.last4Digits}`}
            {supportingCvvInExistingPaymentMethod && props.selected === existingPaymentMethod && (
              <SExistingPaymentMethodCVV value={props.existingPaymentMethodCVV} onFilled={props.onExistingPaymentMethodCVVChanged} />
            )}
          </SExistingPaymentMethodLabel>
        ) : (
          <SExistingPaymentMethodLabel data-dd-privacy={PrivacyOptions.Mask}>{`Remotely created check from account number ending in ${existingPaymentMethod.last4Digits}`}</SExistingPaymentMethodLabel>
        );
      return {
        label,
        value: existingPaymentMethod,
        dataAt: `existingPaymentMethod_${index}`,
        key: `existingPaymentMethod_${index}`,
      };
    });

  return (
    <SExistingPaymentMethodsWrap>
      <SExistingPaymentMethodsInstructions>Which payment method would you like to use?</SExistingPaymentMethodsInstructions>
      <SExisitingPaymentMethodsRadios name="existingPaymentMethods" dataAt="existingPaymentMethods" radioOptions={options} onChange={props.onSelect} value={props.selected} />
    </SExistingPaymentMethodsWrap>
  );
};

const dueDataOptions = new Array(28).fill(null).map((_ignore, index) => ({
  label: ordinal(index + 1),
  value: index + 1,
}));

// tslint:disable-next-line:variable-name
const PaymentDetails: React.FC<IPaymentDetailsProps> = (props) => {
  const [selectedPaymentMethodCategory, selectPaymentMethodCategory] = React.useState<PaymentMethodCategory>(PaymentMethodCategory.NotSelected);
  const [existingPaymentMethod, setExistingPaymentMethod] = React.useState<PaymentMethodProfile>();
  const [existingPaymentMethodCVV, setExistingPaymentMethodCVV] = React.useState<string>('');
  const [debitCardValues, setDebitCardValues] = React.useState<IDebitCardDetails>({
    cardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    hasName: true,
    firstName: '',
    middleName: '',
    lastName: '',
  });
  const [rccValues, setRccValues] = React.useState<IRccDetails>({ routingNumber: '', accountNumber: '' });
  const [dueDate, setDueDate] = React.useState(1);

  React.useEffect(() => {
    selectPaymentMethodCategory(props.selectedPaymentMethodCategory || PaymentMethodCategory.NotSelected);
  }, []);

  const handleChangeExistingPaymentMethod = React.useCallback(
    (selectedExistingPaymentMethod?: PaymentMethodProfile) => {
      setExistingPaymentMethod(selectedExistingPaymentMethod);
      setExistingPaymentMethodCVV(supportingCvvInExistingPaymentMethod ? '' : /* some arbitrary cvv value to pass type validation */ '999');
    },
    [existingPaymentMethodCVV, existingPaymentMethod]
  );

  const handleSelectPaymentMethodCategory = React.useCallback(
    (selectedPaymentMethodCategoryOption: PaymentMethodCategory) => {
      selectPaymentMethodCategory(selectedPaymentMethodCategoryOption);
      handleChangeExistingPaymentMethod();
    },
    [existingPaymentMethodCVV, selectedPaymentMethodCategory]
  );

  const handleExistingPaymentMethodCVV = React.useCallback((value: string) => {
    setExistingPaymentMethodCVV(value);
  }, []);

  const handDueDateChanged = React.useCallback((value: number) => {
    setDueDate(value);
  }, []);

  const handleSubmit = React.useCallback(() => {
    const existingPaymentMethodWithPossibleCVV =
      existingPaymentMethod && existingPaymentMethod.paymentMethodType === PaymentMethodTypes.DEBIT ? { ...existingPaymentMethod, cvv: existingPaymentMethodCVV } : existingPaymentMethod;

    props.submit({
      paymentMethodCategory: selectedPaymentMethodCategory,
      existingPaymentMethod: existingPaymentMethodWithPossibleCVV,
      debitCardDetails: debitCardValues,
      rccDetails: rccValues,
      dueDate,
      applicationId: props.applicationId,
    });
  }, [selectedPaymentMethodCategory, existingPaymentMethod, existingPaymentMethodCVV, debitCardValues, rccValues, dueDate]);

  const isSubmitEnabled = React.useCallback(() => {
    switch (selectedPaymentMethodCategory) {
      case PaymentMethodCategory.ExistingPaymentMethod:
        if (existingPaymentMethod === undefined || isEmpty<undefined>(existingPaymentMethod)) {
          return false;
        }
        if (existingPaymentMethod.paymentMethodType === PaymentMethodTypes.DEBIT && existingPaymentMethodCVV.length < 3) {
          return false;
        }
        return true;
      case PaymentMethodCategory.Debit:
        return validateDebitCardDetails(debitCardValues);
      case PaymentMethodCategory.RCC:
        return validateRccDetails(rccValues);
      case PaymentMethodCategory.MAIL:
        return true;
      case PaymentMethodCategory.NotSelected:
        return false;
      default:
        return false;
    }
  }, [selectedPaymentMethodCategory, existingPaymentMethodCVV, existingPaymentMethod, debitCardValues, rccValues, dueDate]);

  const openRateAndTerms = React.useCallback(() => {
    dataOpen(props.recurringPaymentsData);
  }, [props.recurringPaymentsData]);

  const handleDebitCardValuesChanged = React.useCallback(
    (value, name) => {
      if (name === 'creditCardExpiration') {
        const expirationMonth = value.substring(0, 2);
        const expirationYear = value.substring(2, 4);
        setDebitCardValues({ ...debitCardValues, expirationMonth, expirationYear });
      } else {
        setDebitCardValues({ ...debitCardValues, [name]: value });
      }
    },
    [...Object.values(debitCardValues)]
  );

  const handRccValuesChanged = React.useCallback(
    (value, name) => {
      setRccValues({ ...rccValues, [name]: value });
    },
    [...Object.values(rccValues)]
  );

  const existingPaymentMethodsCategoryProps = {
    title: 'Current payment method on file',
    dataAt: 'existingPaymentMethodsCategory',
    isOpen: selectedPaymentMethodCategory === PaymentMethodCategory.ExistingPaymentMethod,
    onClick: React.useCallback(() => {
      handleSelectPaymentMethodCategory(PaymentMethodCategory.ExistingPaymentMethod);
    }, []),
  };

  const newDebitCardCategoryProps = {
    title: (props.existingPaymentMethods || []).length ? <>New debit card</> : <>Debit card</>,
    dataAt: 'newDebitCardCategory',
    isOpen: selectedPaymentMethodCategory === PaymentMethodCategory.Debit,
    onClick: React.useCallback(() => {
      handleSelectPaymentMethodCategory(PaymentMethodCategory.Debit);
    }, []),
  };

  const newRccCategoryProps = {
    dataAt: 'newRccCategory',
    title: (
      <>
        Remotely created check (RCC){' '}
        <Tooltip
          dataAt="rcc-tooltip"
          width={200}
          position={TooltipPosition.BottomLeft}
          toggler={
            <i className="material-icons" data-at="rcc-tooltip-toggle">
              info
            </i>
          }
        >
          {rccText}
        </Tooltip>
      </>
    ),
    isOpen: selectedPaymentMethodCategory === PaymentMethodCategory.RCC,
    onClick: React.useCallback(() => {
      handleSelectPaymentMethodCategory(PaymentMethodCategory.RCC);
    }, []),
  };

  const mailPaymentCategoryProps = {
    title: 'MLA covered borrowers',
    dataAt: 'mlaCoveredBorrowers',
    isOpen: selectedPaymentMethodCategory === PaymentMethodCategory.MAIL,
    onClick: React.useCallback(() => {
      handleSelectPaymentMethodCategory(PaymentMethodCategory.MAIL);
    }, []),
  };

  const filteredExistingPaymentMethods = !props.militaryLendingAct
    ? props.existingPaymentMethods
    : props.existingPaymentMethods.filter((exisitingPaymentMethod) => exisitingPaymentMethod.paymentMethodType === PaymentMethodTypes.DEBIT);

  return (
    <SPaymentDetails data-dd-privacy={PrivacyOptions.MaskUserInput}>
      <CommonSection>
        <Title data-at="pleaseSelectYourPaymentMethod">How would you like to pay?</Title>
      </CommonSection>

      <CommonSection>
        <SectionTitle data-at="neverForget">With your authorization, we'll automatically debit the minimum payment due on or after your due date.</SectionTitle>
      </CommonSection>

      <CommonSection>
        {filteredExistingPaymentMethods.length > 0 && (
          <CollapsingCategory {...existingPaymentMethodsCategoryProps}>
            <ExistingPaymentMethods
              existingPaymentMethods={filteredExistingPaymentMethods}
              selected={existingPaymentMethod}
              onSelect={handleChangeExistingPaymentMethod}
              existingPaymentMethodCVV={existingPaymentMethodCVV}
              onExistingPaymentMethodCVVChanged={handleExistingPaymentMethodCVV}
            />
          </CollapsingCategory>
        )}
        <CollapsingCategory {...newDebitCardCategoryProps}>
          <CreditCardForm hasName onChange={handleDebitCardValuesChanged} />
        </CollapsingCategory>

        {!props.militaryLendingAct && (
          <CollapsingCategory {...newRccCategoryProps}>
            <SRccFormWrap>
              <RccForm onChange={handRccValuesChanged} error={{ routingNumber: handleRoutingNumValidationError(rccValues.routingNumber) }} />
            </SRccFormWrap>
          </CollapsingCategory>
        )}

        {props.militaryLendingAct && <CollapsingCategory {...mailPaymentCategoryProps}>Mail us a check or money order to Sunbit Payments, PO Box 841238 Los Angeles, CA 90084.</CollapsingCategory>}
      </CommonSection>

      <CommonSection>
        <Title data-at="dueDateTitle">On which day of the month would you like to pay?</Title>
      </CommonSection>

      <CommonSection>
        <SectionTitle data-at="dueDateSubtitle">Your statement closes at least 26 days before your due date. If your due date falls on a weekend or holiday, your payment may be charged on the prior business day. You’ll still have until your due date to make your minimum payment.</SectionTitle>
      </CommonSection>

      <CommonSection>
        <SDueDateSection>
          <SDueDateContent data-dd-privacy={PrivacyOptions.Mask}>
            <Dropdown
              inputColor={'primary'}
              name="dueDate"
              dropdownOptions={dueDataOptions}
              onChange={props.onDueDateChangeMiddleware ? props.onDueDateChangeMiddleware(handDueDateChanged) : handDueDateChanged}
              value={dueDate}
              dataAt="dueDate"
            />
          </SDueDateContent>
        </SDueDateSection>
      </CommonSection>

      <CommonSection>
        <SDisclaimerSmallLegalText dataAt="paymentMethodCategoryConsent">
          Click{' '}
          <CardApplyLinkButton data-at="linkToRecurringPaymentsConsent" onClick={openRateAndTerms}>
            here
          </CardApplyLinkButton>{' '}
          to view the terms of our recurring payment authorization.
        </SDisclaimerSmallLegalText>
      </CommonSection>

      <CommonSection>
        <SDisclaimerLegalText dataAt="paymentMethodAgreeConsent">
          By clicking the <SDisclaimerBoldSpan>&quot;Agree and Submit&quot;</SDisclaimerBoldSpan> button below, you are electronically signing and agreeing to this recurring payment authorization. You
          acknowledge that you have read, printed/saved, understand, and agree to be bound by the terms of this authorization. We will email you a copy of this authorization to your email address.{' '}
          <SDisclaimerBoldSpan>If you do not wish to make recurring payments, you should exit this application.</SDisclaimerBoldSpan>
        </SDisclaimerLegalText>
      </CommonSection>

      <CommonSection>
        <AgreeButton dataAt="paymentMethodSubmit" onClick={handleSubmit} disabled={!isSubmitEnabled()}>
          Agree and Submit
        </AgreeButton>
      </CommonSection>
    </SPaymentDetails>
  );
};

export default PaymentDetails;
